import React, { useState, useEffect, useContext } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { LoadingContext } from '../LoadingContext';
import ProductCard from './ProductCard';
import CategoryCard from './CategoryCard';
import CategoryList from './CategoryList';
import './SearchResults.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function SearchResults() {
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('q') || '';
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoryTree, setCategoryTree] = useState([]);
  const [allCategoryTree, setAllCategoryTree] = useState([]);
  const [categorySearchTerm, setCategorySearchTerm] = useState('');
  const { setIsLoading } = useContext(LoadingContext);
  const [noResults, setNoResults] = useState(false);
  const [isExpandedView] = useState(true);
  const [promoMap, setPromoMap] = useState({});

  useEffect(() => {
    const fetchSearchResults = async () => {
      if (!searchQuery) return;

      setIsLoading(true);
      try {
        const productsResponse = await fetch(`${API_BASE_URL}/Products?SearchString=${encodeURIComponent(searchQuery)}`);
        const productsData = await productsResponse.json();

        const categoriesResponse = await fetch(`${API_BASE_URL}/api/categories/search?term=${encodeURIComponent(searchQuery)}`);
        const categoriesData = await categoriesResponse.json();

        const categoryTreeResponse = await fetch(`${API_BASE_URL}/api/categories/tree`);
        const categoryTreeData = await categoryTreeResponse.json();

        setProducts(productsData.products || []);
        setCategories(categoriesData || []);
        setCategoryTree(categoryTreeData || []);
        setAllCategoryTree(categoryTreeData || []);

        setNoResults((productsData.products?.length === 0 || !productsData.products) &&
          (categoriesData?.length === 0 || !categoriesData));
      } catch (error) {
        // Silent fail (previously only 1 console.error line in this catch block, no other actions)
      } finally {
        setIsLoading(false);
      }
    };

    fetchSearchResults();

    fetch(`${API_BASE_URL}/api/Promotions`)
      .then(r => r.ok ? r.json() : [])
      .then(promos => {
        const map = {};
        promos.forEach(p => {
          // Standalone promos are not catalogue products - they have no id to key on
          if (!p.productId && !p.variantId) return;
          map[p.variantId ? `v${p.variantId}` : `p${p.productId}`] = p.promotionPrice;
        });
        setPromoMap(map);
      })
      .catch(() => {});
  }, [searchQuery, setIsLoading]);

  return (
    <div className="search-results-container">
      <div className="search-results-breadcrumb">
        <Link to="/">Home</Link>{" "}
        &gt; <span>Search Results for "{searchQuery}"</span>
      </div>

      <div className="search-results-main-container">
        {/* Category List Sidebar */}
        <aside className={`search-results-sidebar ${!isExpandedView ? 'collapsed' : ''}`}>
          <div className="search-results-sidebar-top">
            <h3 className="search-results-sidebar-heading">Categories</h3>
          </div>

          {/* Category Search Bar
          {isExpandedView && (
            <div className="category-search-container">
              <input
                type="text"
                className="category-search-input"
                placeholder="Search categories..."
                value={categorySearchTerm}
                onChange={(e) => searchSidebarCategories(e.target.value)}
              />
            </div>
          )} */}

          {isExpandedView && (
            <CategoryList key="search-category-list" categories={categoryTree} />
          )}
        </aside>

        {/* Main Content */}
        <main className={`search-results-content ${!isExpandedView ? 'expanded' : ''}`}>
          {noResults ? (
            <div className="no-results-message">
              <p>No products or categories found matching your search.</p>
              <p>Try a different search term or <Link to="/category">browse all products</Link>.</p>
            </div>
          ) : (
            <>
              {categories.length > 0 && (
                <div className="search-results-section">
                  <h2>Categories</h2>
                  <div className="search-results-grid">
                    {categories.map(category => (
                      <div className="search-results-card" key={category.categoryID}>
                        <CategoryCard category={category} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {products.length > 0 && (
                <div className="search-results-section">
                  <h2>Products</h2>
                  <div className="search-results-grid">
                    {products.map(product => (
                      <div className="search-results-card" key={product.productID}>
                        <ProductCard product={product} promoPrice={promoMap[`p${product.productID}`]} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}

export default SearchResults;